import React from 'react'
import { styled, forwardRef, FloraVariants } from '@grupoboticario/flora-react-system'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraradio-guia-de-migra%C3%A7%C3%A3o--page | Acesse o guia de migração} para saber como migrar para a nova versão.
 */
const StyledIndicator = styled(RadioGroupPrimitive.Indicator, {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  width: '100%',
  position: 'relative',
  '&::after': {
    content: '""',
    display: 'block',
    width: '8px',
    height: '8px',
    borderRadius: '$pill',
    overflow: 'hidden',
  },
})

/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraradio-guia-de-migra%C3%A7%C3%A3o--page | Acesse o guia de migração} para saber como migrar para a nova versão.
 */
const StyledRadio = styled(RadioGroupPrimitive.Item, {
  appearance: 'none',
  border: 'none',
  padding: 0,
  width: 16,
  minWidth: 16,
  height: 16,
  borderRadius: '$pill',
  '&:not(:disabled), &:not(:disabled) + label': {
    cursor: 'pointer',
  },
  variants: {
    size: {
      small: {
        '& + label': {
          fontSize: '$auxiliar',
        },
      },
      medium: {
        '& + label': {
          fontSize: '$bodySmall',
        },
      },
      large: {
        '& + label': {
          fontSize: '$bodyLarge',
        },
      },
    },
  },
  defaultVariants: {
    size: 'standard',
  },
})

/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraradio-guia-de-migra%C3%A7%C3%A3o--page | Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export const StyledRadioGroup = styled(RadioGroupPrimitive.Root, {
  variants: {
    variant: {
      standard: {
        '& + label': {
          color: '$colors$nonInteractivePredominant',
        },
        [`& ${StyledRadio}`]: {
          boxShadow: 'inset 0 0 0 2px $colors$nonInteractivePredominant',
          backgroundColor: 'transparent',
          '& + label': {
            color: '$colors$nonInteractivePredominant',
          },
        },
        [`& ${StyledIndicator}::after`]: {
          backgroundColor: '$actionableDefault',
        },
        '&:hover': {
          [`& ${StyledRadio}`]: {
            boxShadow: 'inset 0 0 0 2px $colors$actionableHover',
          },
          [`& ${StyledIndicator}::after`]: {
            backgroundColor: '$colors$actionableHover',
          },
        },
        [`& ${StyledRadio}[data-disabled]`]: {
          color: '$colors$disabledContent',
          backgroundColor: '$colors$disabledBackground',
          boxShadow: 'inset 0 0 0 2px currentColor',
          '& + label': {
            color: '$colors$disabledContent',
          },
          [`& ${StyledIndicator}::after`]: {
            backgroundColor: '$colors$disabledContent',
          },
        },
      },
      alt: {
        color: '$colors$nonInteractiveAltPredominant',
        [`& ${StyledRadio}`]: {
          boxShadow: 'inset 0 0 0 2px $colors$actionableAlt',
          backgroundColor: 'transparent',
          '& + label': {
            color: '$colors$nonInteractiveAltPredominant',
          },
        },
        [`& ${StyledIndicator}::after`]: {
          backgroundColor: '$colors$actionableAltContent',
        },
        '&:hover': {
          [`& ${StyledRadio}`]: {
            boxShadow: 'inset 0 0 0 2px $colors$actionableAltHover',
          },
          [`& ${StyledIndicator}::after`]: {
            backgroundColor: '$colors$actionableAltHover',
          },
        },
        [`& ${StyledRadio}[data-disabled]`]: {
          color: '$colors$disabledAltContent',
          backgroundColor: '$colors$disabledAltBackground',
          boxShadow: 'inset 0 0 0 2px $colors$disabledAltContent',
          '& + label': {
            color: '$colors$disabledAltContent',
          },
          [`& ${StyledIndicator}::after`]: {
            backgroundColor: '$colors$disabledAltContent',
          },
        },
      },
    },
  },
})

/**
 * @deprecated Esta tipagem refere-se a um componente que não receberá mais atualizações.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraradio-guia-de-migra%C3%A7%C3%A3o--page | Acesse essa documentação} e saiba como migrar para a V2.
 */
export type RadioGroupProps = HTMLFloraProps<'div'> &
  FloraVariants<typeof StyledRadioGroup> &
  RadioGroupPrimitive.RadioGroupProps

/**
 * @component
 * @name RadioGroup
 * @description RadioGroup component
 * @example
 * <RadioGroup>
 *   <Radio aria-label="Value 1" value="1" />
 *   <Radio aria-label="Value 2" value="2" />
 *   <Text as="label">Text example</Text>
 * </RadioGroup>
 * @tutorial https://flora.gb.tech/docs/components/core/radio
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraradio-guia-de-migra%C3%A7%C3%A3o--page | Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export const RadioGroup = forwardRef<'div', RadioGroupProps>((props, forwardedRef) => {
  return <StyledRadioGroup ref={forwardedRef} {...props} />
})

/**
 * @deprecated Esta tipagem refere-se a um componente que não receberá mais atualizações.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraradio-guia-de-migra%C3%A7%C3%A3o--page | Acesse essa documentação} e saiba como migrar para a V2.
 */
export type RadioProps = FloraVariants<typeof StyledRadio> & RadioGroupPrimitive.RadioProps

/**
 * @component
 * @name Radio
 * @description Radio component
 * @example
 * <RadioGroup>
 *   <Radio aria-label="Value 1" value="1" />
 *   <Radio aria-label="Value 2" value="2" />
 *   <Text as="label">Text example</Text>
 * </RadioGroup>
 * @tutorial https://flora.gb.tech/docs/components/core/radio
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraradio-guia-de-migra%C3%A7%C3%A3o--page | Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export const Radio = forwardRef<'button', RadioProps>((props, forwardedRef) => {
  return (
    <StyledRadio ref={forwardedRef} data-flora="radio" {...props}>
      <StyledIndicator />
    </StyledRadio>
  )
})
